/**
 * Shared primitives, styled after the annotated product mockup: white cards
 * with soft corners, sentence-case card titles with an optional one-line
 * subtitle, outline and primary buttons, tinted status pills, signed deltas
 * with an arrow (never colour alone), and empty/error states that say why.
 * Controls (buttons, fields, segmented controls, icons) live in ./ui.
 */

import { TONES } from "../lib/tones.js";
import { Icon } from "./ui/Icon.jsx";

export { Button } from "./ui/Button.jsx";

/** `interactive` lifts the card on hover, for cards that are themselves a link or button. */
export function Card({ children, className = "", padded = true, interactive = false }) {
  return (
    <section
      className={`rounded-card bg-surface-1 shadow-card ${interactive ? "transition-shadow duration-200 hover:shadow-card-hover" : ""} ${padded ? "p-5" : ""} ${className}`}
    >
      {children}
    </section>
  );
}

export function SectionTitle({ children, hint, actions, subtitle }) {
  return (
    // Centred: the title and its buttons share one line; a subtitle grows the title block.
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <h2 className="text-[15.5px] font-semibold tracking-[-0.01em] text-ink">{children}</h2>
          {hint && (
            <span className="cursor-help text-ink-3 hover:text-ink-2" title={hint} aria-label={hint} role="img">
              <Icon name="info" className="size-3.5" />
            </span>
          )}
        </div>
        {subtitle && <p className="mt-0.5 text-[12.5px] text-ink-3">{subtitle}</p>}
      </div>
      {actions && <div className="ml-auto flex shrink-0 flex-wrap items-center gap-1.5">{actions}</div>}
    </div>
  );
}

/** A status as a tinted pill. The text carries the meaning; the colour only groups. */
export function StatusPill({ children, tone = "neutral" }) {
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap" style={TONES[tone] ?? TONES.neutral}>
      {children}
    </span>
  );
}

/** Period-over-period delta, always signed, basis in the tooltip. */
export function DeltaBadge({ current, previous, basis, higherIsBetter = true }) {
  if (!(previous > 0)) {
    return (
      <span className="text-[11px] text-ink-3" title="No prior period to compare">
        —
      </span>
    );
  }
  // A near-zero base (e.g. content published during this period) makes the
  // percentage meaningless ("+132650%"), so it reads as "new" instead.
  if (current > 0 && previous < current * 0.05) {
    return (
      <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[11px] font-medium text-ink-3" title={`Previous period ${previous} vs ${current} now (${basis})`}>
        new
      </span>
    );
  }
  const change = (current - previous) / previous;
  const flat = Math.abs(change) < 0.005;
  const good = higherIsBetter ? change > 0 : change < 0;
  const tone = flat ? TONES.neutral : good ? TONES.good : { color: "var(--status-serious)", background: "var(--status-serious-bg)" };
  const arrow = flat ? "▬" : change > 0 ? "▲" : "▼";
  const pct = `${change > 0 ? "+" : change < 0 ? "−" : ""}${Math.abs(change * 100).toFixed(Math.abs(change) < 0.1 ? 1 : 0)}%`;
  return (
    <span className="tabular inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold" style={tone} title={`${pct} vs ${basis}`}>
      <span aria-hidden className="text-[7px]">
        {arrow}
      </span>
      {pct}
    </span>
  );
}

export function EmptyState({ title, detail }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-card border border-dashed border-line-default bg-surface-2/40 px-4 py-8 text-center">
      <p className="text-[13px] font-medium text-ink">{title}</p>
      {detail && <p className="max-w-md text-[12px] text-ink-3">{detail}</p>}
    </div>
  );
}

export function ErrorState({ what, message, onRetry, retryable = true }) {
  return (
    <div className="flex gap-3 rounded-card border px-4 py-3" style={{ borderColor: "rgb(184 51 43 / 0.25)", background: "var(--status-critical-bg)", color: "var(--status-critical)" }} role="alert">
      <Icon name="alert" className="mt-0.5 size-4" />
      <div className="min-w-0">
        <p className="text-[13px] font-medium">Source unavailable: cannot evaluate {what}</p>
        <p className="mt-0.5 text-[12px] text-ink-2">{message}</p>
        {onRetry && retryable && (
          <button type="button" onClick={onRetry} className="mt-2 h-7 rounded-control border border-line-default bg-surface-1 px-2.5 text-[11.5px] font-medium text-ink hover:border-line-strong">
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A page-level message in place of content: no access, could not load,
 * nothing to show. `icon` names an Icon; `action` is an optional control.
 */
export function StatePanel({ title, detail, icon = "lock", action, className = "" }) {
  return (
    <div className={`mx-auto mt-16 flex max-w-md animate-rise flex-col items-center rounded-card bg-surface-1 px-6 py-8 text-center shadow-card ${className}`}>
      <span className="mb-3 grid size-11 place-items-center rounded-full bg-surface-2 text-ink-2">
        <Icon name={icon} className="size-5" />
      </span>
      {title && <h1 className="text-[15px] font-semibold text-ink">{title}</h1>}
      {detail && <p className="mt-1 text-[12.5px] text-ink-3">{detail}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Skeleton({ height = 16, width = "100%", className = "" }) {
  return <div className={`skeleton ${className}`} style={{ height, width }} />;
}

/**
 * Small quiet button used in card headers. `icon` names an Icon shown before the text.
 * It has a light grey fill so it reads as a button on a white card; `onPage` gives it
 * a white fill with the card edge instead, for when it sits on the grey page itself.
 */
export function GhostButton({ children, onClick, pressed, title, disabled, icon, onPage = false }) {
  const rest = onPage ? "bg-surface-1 text-ink-2 shadow-card hover:text-ink hover:shadow-card-hover" : "bg-surface-2 text-ink-2 hover:bg-[#eceef3] hover:text-ink";
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      disabled={disabled}
      aria-pressed={pressed}
      className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-control px-3 text-[11.5px] font-medium whitespace-nowrap transition-[background-color,box-shadow,color] ${pressed ? "bg-accent-soft text-accent-ink" : rest} disabled:opacity-40`}
    >
      {icon && <Icon name={icon} className="size-3.5" />}
      {children}
    </button>
  );
}
